import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { useNavigate } from "@tanstack/react-router"
import { memo, type ReactElement, useCallback } from "react"

import { modelIconMapping } from "../../mappings/icons"
import { sdk } from "../../sdk"

interface UserSettingsDropdownMenuProps
	extends Omit<MenuRootProps, "children">, Omit<MenuClickTriggerProps, "children" | "ref">, MenuVariantProps {
	children: ReactElement
}

export const UserSettingsDropdownMenu = memo((props: UserSettingsDropdownMenuProps) => {
	const { children, disabled, radius, size, variant, accentColor, ...rest } = props

	const navigate = useNavigate()

	const handleViewSettings = useCallback(() => {
		void navigate({ from: "/$organizationId/$projectId", to: "/$organizationId/settings/organization" })
	}, [navigate])

	const handleViewPreferences = useCallback(() => {
		void navigate({ from: "/$organizationId/$projectId", to: "/$organizationId/settings/accounts/preferences" })
	}, [navigate])

	// The confirmation that used to live here is now `MainService.logout`'s, so that the sign-outs which never pass
	// through this menu ask about unsynced work too.
	const handleSignOut = useCallback(() => {
		void sdk.main.logout()
	}, [])

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content size={size} radius={radius} accentColor={accentColor} variant={variant}>
				<Menu.Item onSelect={handleViewSettings}>
					<LuIcon icon={modelIconMapping.settings} />
					Settings
				</Menu.Item>
				<Menu.Item onSelect={handleViewPreferences}>
					<LuIcon icon={modelIconMapping.preferences} />
					Preferences
				</Menu.Item>
				<Menu.Separator />
				<Menu.Item onSelect={handleSignOut}>
					<LuIcon icon={modelIconMapping.exit} />
					Sign out
				</Menu.Item>
			</Menu.Content>
		</Menu.Root>
	)
})
UserSettingsDropdownMenu.displayName = "UserSettingsDropdownMenu"
